// packages/core/src/learning/core-modules/money-psychology.ts
// ─────────────────────────────────────────────────────────────────────────
// NISK CORE PATH — Module 09: "Money Psychology" (tier: growth)
// 5 lessons + checkpoint quiz + final test. Assembled to the real
// Lesson / ModuleAssessment / LearningModule types.
//
// Impact Lab links (verified present): 'lifestyle-creep', 'daily-coffee'.
// Pillar is 'money-psychology-core' (distinct from legacy 'money-psychology').
// ─────────────────────────────────────────────────────────────────────────

import type {
	Lesson,
	LearningModule,
	ModuleAssessment,
} from '../LearningTypes';

const LESSON_1: Lesson = {
	id: 'money-psychology--money-is-emotional-first',
	title: 'Money is emotional first',
	description:
		'You learn why money decisions are shaped by fear, pride, stress, memory, and identity before math gets involved.',
	pillar: 'money-psychology-core',
	schoolsOfThought: [
		'money-psychology-housel',
		'behavioral-finance',
		'personal-finance-basics',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['money-psychology', 'emotions', 'behavior', 'decision-making'],
	prerequisites: [],
	relatedLessons: ['money-psychology--lifestyle-creep-is-quiet'],
	concepts: ['emotional-money', 'money-identity', 'behavior-before-math'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Money looks like math from the outside. Income minus expenses. Debt balances. Interest rates. Savings rate. Net worth. But in real life, money usually hits your nervous system before it hits your spreadsheet.',
		},
		{
			type: 'paragraph',
			text: 'A bill can feel like shame. A raise can feel like permission. Debt can feel like failure. Saving can feel safe to one person and restrictive to another. Spending can feel careless, generous, powerful, comforting, or deserved depending on what the money means to you.',
		},
		{
			type: 'callout',
			icon: '🧠',
			text: 'The math matters, but your behavior decides whether the math survives contact with real life.',
		},
		{
			type: 'bullet_list',
			items: [
				'Fear can make you avoid looking',
				'Pride can make you overspend',
				'Stress can make small comforts feel urgent',
				'Past scarcity can make saving feel like survival',
				'Social pressure can make spending feel normal',
			],
		},
		{
			type: 'example',
			label: 'Same purchase, different meaning',
			text: 'Two people spend $120 on dinner. One planned it, enjoyed it, and stayed on track. The other put it on a credit card because they felt left out and did not want to say no. Same dollars. Different psychology. Different result.',
		},
		{
			type: 'paragraph',
			text: 'This is why personal finance advice often fails. It assumes people are calm, rational, and consistent. Real people are tired. They compare themselves. They carry family stories. They avoid uncomfortable numbers. They want to feel in control. They want to feel like they are not behind.',
		},
		{
			type: 'paragraph',
			text: 'Better money behavior starts when you stop pretending you are a robot. Instead of asking only, “What is the correct financial move?” also ask, “What emotion is driving this?” That one question can slow down a lot of bad decisions.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is thinking a budget will fix what is really a stress, identity, or avoidance pattern.',
		},
		{
			type: 'key_takeaway',
			text: 'Money is math plus emotion. Ignore the emotion and the math usually breaks.',
		},
	],
	audioScript:
		'Money is emotional before it is mathematical. That is not weakness. That is being human. A spreadsheet can tell you what would make sense, but your actual decision is shaped by stress, pride, fear, comparison, family history, and what money represents to you. Debt might feel like failure. Saving might feel like safety. Spending might feel like freedom. Or spending might feel like proof that you are doing okay. That emotional meaning changes behavior. This is why two people can make the same purchase and have completely different outcomes. One person plans a $120 dinner and enjoys it without breaking anything. Another spends the same $120 because they felt embarrassed to say no, then feels worse afterward. Same dollars. Different psychology. The point is not to shame emotion. The point is to notice it. If you do not notice the emotion, you will keep calling emotional decisions “logic.” You will say, “I deserve this,” when you are really stressed. You will say, “I will look later,” when you are really afraid. You will say, “Everyone spends this much,” when you are really comparing. The math still matters. But behavior decides if the math gets followed. A good money system makes room for your real brain, not your fantasy robot brain. Before a money decision, ask: what feeling is driving this? That pause is powerful.',
	flashcards: [
		{
			id: 'money-psychology--money-is-emotional-first--fc-main',
			role: 'main_idea',
			front: 'Why is money emotional?',
			back: 'Because money connects to fear, pride, stress, safety, identity, and social pressure.',
		},
		{
			id: 'money-psychology--money-is-emotional-first--fc-watch',
			role: 'watch_out',
			front: 'What breaks the math?',
			back: 'Unnoticed emotions can turn good plans into avoided or impulsive decisions.',
		},
		{
			id: 'money-psychology--money-is-emotional-first--fc-use',
			role: 'use_it',
			front: 'What should you ask first?',
			back: 'Ask what emotion is driving the money decision before acting.',
		},
	],
	applyAction: {
		type: 'noToolAction',
		label: 'Name your money emotion',
		prompt: 'Pick one recent money decision and name the emotion that was driving it.',
	},
	revision: 1,
};

const LESSON_2: Lesson = {
	id: 'money-psychology--lifestyle-creep-is-quiet',
	title: 'Lifestyle creep is quiet',
	description:
		'You learn how rising income can disappear into new normal spending before it creates freedom.',
	pillar: 'money-psychology-core',
	schoolsOfThought: [
		'behavioral-finance',
		'rich-life-ramit',
		'income-expansion',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['lifestyle-creep', 'spending', 'raises', 'behavior'],
	prerequisites: ['money-psychology--money-is-emotional-first'],
	relatedLessons: ['money-psychology--small-leaks-become-identity'],
	concepts: ['lifestyle-creep', 'new-normal-spending', 'raise-protection'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Lifestyle creep rarely feels dramatic. It usually feels reasonable. You earn a little more, so you upgrade a few things. Better food. Better clothes. More subscriptions. More convenience. More weekends out. Nothing feels wild by itself.',
		},
		{
			type: 'paragraph',
			text: 'Then one day your income is higher, but your stress is the same. You make more money, but you do not feel more free. The raise happened. The options did not.',
		},
		{
			type: 'callout',
			icon: '🐍',
			text: 'Lifestyle creep is dangerous because it hides inside normal upgrades.',
		},
		{
			type: 'bullet_list',
			items: [
				'A raise becomes a payment',
				'Overtime becomes takeout',
				'A bonus becomes random shopping',
				'A promotion becomes a more expensive lifestyle',
				'A better income becomes a higher survival cost',
			],
		},
		{
			type: 'example',
			label: 'The $500 raise',
			text: 'Suppose your take-home pay rises by $500 per month. If $500 becomes new monthly spending, your life got more expensive but not more flexible. If $250 improves life now and $250 builds runway or goals, the raise starts buying options.',
		},
		{
			type: 'paragraph',
			text: 'This does not mean upgrades are bad. A nicer apartment, better food, safer car, gym membership, therapy, travel, hobbies, or convenience can genuinely improve life. The issue is not spending. The issue is automatic spending that raises your baseline without a decision.',
		},
		{
			type: 'paragraph',
			text: 'Your baseline is the monthly cost of being you. The higher your baseline, the more income you need just to stay afloat. Lifestyle creep quietly raises that baseline. That can make you feel trapped even when you earn more.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is letting every income increase become permanent spending before you decide what freedom you wanted it to buy.',
		},
		{
			type: 'key_takeaway',
			text: 'A raise only changes your options if you keep part of it from becoming your new normal.',
		},
	],
	audioScript:
		'Lifestyle creep is not usually one huge bad decision. It is quieter than that. It is the small upgrades that feel normal after your income rises. Better food. More convenience. Nicer clothes. More subscriptions. More weekends out. A slightly better apartment. A slightly better car. None of it feels crazy. The problem is what happens when all of it becomes your new baseline. Your baseline is the monthly cost of being you. If that baseline rises every time your income rises, you can earn more and still feel trapped. That is the strange part. You got the raise, but you did not get the freedom. The money just changed categories. It used to be extra. Now it is required. This does not mean you should never upgrade your life. That would miss the point. Money should make life better. The goal is not to be miserable. The goal is to make upgrades on purpose. When new income arrives, give it a job before your habits do. Maybe part of it improves your life now. Maybe part builds emergency runway. Maybe part reduces debt pressure. Maybe part goes toward long-term goals. The split is not the lesson. The lesson is to decide before lifestyle creep decides for you. If every raise becomes a new bill, your income went up but your options did not. Keep some of the raise free, and it can start buying back time, calm, and choices.',
	flashcards: [
		{
			id: 'money-psychology--lifestyle-creep-is-quiet--fc-main',
			role: 'main_idea',
			front: 'What is lifestyle creep?',
			back: 'When higher income quietly becomes higher normal spending.',
		},
		{
			id: 'money-psychology--lifestyle-creep-is-quiet--fc-watch',
			role: 'watch_out',
			front: 'Why is creep dangerous?',
			back: 'It raises your monthly baseline and can erase the freedom from a raise.',
		},
		{
			id: 'money-psychology--lifestyle-creep-is-quiet--fc-use',
			role: 'use_it',
			front: 'How do you protect a raise?',
			back: 'Assign part of it to options before habits turn it into spending.',
		},
	],
	applyAction: {
		type: 'openImpactScenario',
		label: 'Test lifestyle creep',
		scenarioId: 'lifestyle-creep',
	},
	revision: 1,
};

const LESSON_3: Lesson = {
	id: 'money-psychology--small-leaks-become-identity',
	title: 'Small leaks become identity',
	description:
		'You learn why repeated tiny purchases matter less as guilt and more as a clue about your automatic habits.',
	pillar: 'money-psychology-core',
	schoolsOfThought: [
		'behavioral-finance',
		'frugality',
		'money-psychology-housel',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['habits', 'daily-spending', 'coffee', 'spending-leaks'],
	prerequisites: ['money-psychology--lifestyle-creep-is-quiet'],
	relatedLessons: ['money-psychology--future-self-gets-a-vote'],
	concepts: ['daily-leaks', 'automatic-spending', 'habit-cost'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Small purchases are not evil. Coffee is not evil. Snacks are not evil. Convenience is not evil. The problem is not the item. The problem is when repeated spending becomes invisible.',
		},
		{
			type: 'paragraph',
			text: 'A daily leak is a small purchase that happens so often it becomes part of your identity. “This is just what I do.” That sentence can be fine if the habit is chosen and affordable. It becomes dangerous when the habit is automatic and your bigger goals never get a vote.',
		},
		{
			type: 'callout',
			icon: '☕',
			text: 'The goal is not to shame small joys. The goal is to notice which small habits are quietly voting against your options.',
		},
		{
			type: 'bullet_list',
			items: [
				'Small spending can be intentional',
				'Small spending can be emotional',
				'Small spending can be social',
				'Small spending can be automatic',
				'Small spending can be worth it or not worth it',
			],
		},
		{
			type: 'example',
			label: '$8 most weekdays',
			text: 'An $8 habit five days a week is about $160 per month. That might be completely fine if it fits your plan and genuinely improves your day. It is a problem if you say you cannot build runway while $160 disappears without awareness.',
		},
		{
			type: 'paragraph',
			text: 'This is where people get the lesson wrong. They hear “small leaks” and think the answer is to cut every pleasure. That is not the point. Cutting everything usually fails because it makes life feel tiny. A better move is to rank the habit honestly. Does it give you real value, or is it just a reflex?',
		},
		{
			type: 'paragraph',
			text: 'If the habit gives real value, keep it and plan for it. If it is mostly stress, boredom, social pressure, or convenience you barely enjoy, test reducing it. Not forever. Just long enough to see what changes.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is defending every small purchase individually while ignoring what the pattern costs monthly.',
		},
		{
			type: 'key_takeaway',
			text: 'Small leaks matter when they are automatic, repeated, and stealing from goals you care about more.',
		},
	],
	audioScript:
		'Small purchases get talked about in a really annoying way. People act like coffee is the reason everyone is broke. That is too simple. Coffee is not the villain. Snacks are not the villain. Convenience is not the villain. The real issue is automatic spending. A small purchase becomes powerful when it repeats often enough to become invisible. You stop deciding. You just do it. And when a habit becomes automatic, your bigger goals might never get a vote. An $8 habit five days a week is around $160 a month. That might be worth it. Maybe it makes your morning better, helps you work, gives you a ritual, or fits easily into your plan. Fine. Keep it. But if you say you cannot build a cushion, cannot pay extra on debt, cannot fund a goal, and that $160 is disappearing without thought, that is useful information. The point is not guilt. Guilt is usually a bad money system. The point is awareness. Ask: do I actually value this, or is it stress, boredom, convenience, or social pressure? If you value it, plan for it. If you barely notice it, test cutting it or shrinking it. Not because small joys are bad, but because invisible habits can quietly vote against your future self. Small leaks matter when they are automatic and repeated. Make them visible, then choose.',
	flashcards: [
		{
			id: 'money-psychology--small-leaks-become-identity--fc-main',
			role: 'main_idea',
			front: 'What is a daily leak?',
			back: 'A repeated small purchase that becomes automatic and invisible.',
		},
		{
			id: 'money-psychology--small-leaks-become-identity--fc-watch',
			role: 'watch_out',
			front: 'What is the small-purchase trap?',
			back: 'Defending each purchase alone while ignoring the monthly pattern.',
		},
		{
			id: 'money-psychology--small-leaks-become-identity--fc-use',
			role: 'use_it',
			front: 'How should you judge it?',
			back: 'Ask whether the habit gives real value or just runs on reflex.',
		},
	],
	applyAction: {
		type: 'openImpactScenario',
		label: 'Test a daily habit cost',
		scenarioId: 'daily-coffee',
	},
	revision: 1,
};

const LESSON_4: Lesson = {
	id: 'money-psychology--future-self-gets-a-vote',
	title: 'Your future self gets a vote',
	description:
		'You learn how to balance present comfort with future freedom without turning money into punishment.',
	pillar: 'money-psychology-core',
	schoolsOfThought: ['money-psychology-housel', 'life-design', 'fi-math'],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['future-self', 'habits', 'planning', 'freedom'],
	prerequisites: ['money-psychology--small-leaks-become-identity'],
	relatedLessons: ['money-psychology--enough-is-a-money-skill'],
	concepts: ['future-self', 'present-bias', 'delayed-options'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Your present self is loud. It wants comfort, relief, status, fun, convenience, and proof that life is not just work. Your future self is quieter. It needs runway, fewer fixed bills, fewer regrets, and more options.',
		},
		{
			type: 'paragraph',
			text: 'Most money decisions are a negotiation between those two selves. If your present self always wins, future you inherits stress. If your future self always wins, present you may feel punished and eventually rebel.',
		},
		{
			type: 'callout',
			icon: '⚖️',
			text: 'Good money behavior is not about ignoring today. It is about giving tomorrow a real vote.',
		},
		{
			type: 'bullet_list',
			items: [
				'Present self asks: What feels good now?',
				'Future self asks: What does this cost later?',
				'Present self needs joy',
				'Future self needs options',
				'Your plan has to respect both',
			],
		},
		{
			type: 'example',
			label: 'The $200 decision',
			text: 'Spending $200 on something you value can be completely reasonable. Spending $200 automatically because you were stressed may feel good for one night and annoying for months. The difference is whether both selves got a vote.',
		},
		{
			type: 'paragraph',
			text: 'Present bias is the human habit of overvaluing what feels good now and undervaluing what helps later. You do not beat it by yelling at yourself. You beat it by creating friction, rules, and defaults that protect future you when present you is tired.',
		},
		{
			type: 'paragraph',
			text: 'That might mean waiting 24 hours before nonessential purchases, separating goal money from spending money, deciding raise splits ahead of time, or creating a “fun money” category so enjoyment has boundaries instead of guilt.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is calling every present desire “self-care” and every future commitment “restriction.”',
		},
		{
			type: 'key_takeaway',
			text: 'Your best money decisions protect future you without making present you hate the plan.',
		},
	],
	audioScript:
		'Your present self is loud. It wants relief right now. It wants comfort, fun, convenience, status, and a break from pressure. That is normal. Your future self is quieter, but future you is the one who has to live with the payment, the missing savings, the higher baseline, or the extra stress. Money behavior is basically a negotiation between those two versions of you. If present you always wins, future you gets handed the bill. If future you always wins, present you feels trapped and eventually rebels. The goal is balance. Not fake balance where you spend everything and call it self-care. Not fake discipline where you never enjoy your life. Real balance means tomorrow gets a vote. Before a money decision, ask: what does this give me now, and what does it cost me later? That question is simple, but it changes the whole decision. A $200 purchase might be worth it if it gives real value and fits your plan. The same $200 might be a problem if it is just stress spending wearing a costume. Present bias makes now feel bigger than later. You handle that by building friction and defaults. Wait 24 hours. Separate goal money. Decide raise splits early. Give yourself planned fun money. The point is to protect future you without making present you miserable. That is the sweet spot.',
	flashcards: [
		{
			id: 'money-psychology--future-self-gets-a-vote--fc-main',
			role: 'main_idea',
			front: 'Who needs a vote?',
			back: 'Your future self needs a real vote in present money decisions.',
		},
		{
			id: 'money-psychology--future-self-gets-a-vote--fc-watch',
			role: 'watch_out',
			front: 'What is present bias?',
			back: 'Overvaluing what feels good now and undervaluing what helps later.',
		},
		{
			id: 'money-psychology--future-self-gets-a-vote--fc-use',
			role: 'use_it',
			front: 'What question helps?',
			back: 'Ask what this gives now and what it costs later.',
		},
	],
	applyAction: {
		type: 'runFreedomCost',
		label: 'Test a $200 choice',
		amount: 200,
	},
	revision: 1,
};

const LESSON_5: Lesson = {
	id: 'money-psychology--enough-is-a-money-skill',
	title: 'Enough is a money skill',
	description:
		'You learn why defining “enough” protects you from endless comparison, overwork, and spending without direction.',
	pillar: 'money-psychology-core',
	schoolsOfThought: ['money-psychology-housel', 'minimalism', 'life-design'],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['enough', 'comparison', 'values', 'life-design'],
	prerequisites: ['money-psychology--future-self-gets-a-vote'],
	relatedLessons: ['money-psychology--money-is-emotional-first'],
	concepts: ['enough-point', 'comparison-control', 'values-based-money'],
	blocks: [
		{
			type: 'paragraph',
			text: 'More is an easy target because it never asks you to think. More income. More stuff. More status. More upgrades. More proof. More options. More can be useful, but without a definition of enough, more becomes a treadmill.',
		},
		{
			type: 'paragraph',
			text: 'Enough does not mean giving up ambition. It means knowing what money is supposed to do for your life. Without that, you can keep earning, spending, and chasing while still feeling behind.',
		},
		{
			type: 'callout',
			icon: '🛑',
			text: 'If you never define enough, comparison will define it for you.',
		},
		{
			type: 'bullet_list',
			items: [
				'Enough income to cover needs and goals',
				'Enough runway to sleep better',
				'Enough spending to enjoy life',
				'Enough ambition to keep growing',
				'Enough boundaries to stop chasing every signal',
			],
		},
		{
			type: 'example',
			label: 'The moving finish line',
			text: 'Someone making $45,000 thinks $70,000 will finally feel calm. Then at $70,000, their apartment, car, trips, and social circle change. Now $100,000 feels necessary. Sometimes the problem is not income. It is an undefined finish line.',
		},
		{
			type: 'paragraph',
			text: 'This is one of the hardest money psychology lessons because comparison is everywhere. You see other people’s trips, cars, apartments, weddings, watches, business wins, and investment screenshots. You rarely see the debt, stress, family help, risk, or tradeoffs behind them.',
		},
		{
			type: 'paragraph',
			text: 'Enough is personal, but it should be concrete. Enough runway might be three months of expenses. Enough lifestyle might mean eating out twice a week without guilt. Enough ambition might mean building income while protecting health. Enough freedom might mean being able to leave a bad job without panic.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is using someone else’s highlight reel as your financial target.',
		},
		{
			type: 'key_takeaway',
			text: 'Enough is not the enemy of ambition. It is the boundary that keeps ambition from owning you.',
		},
	],
	audioScript:
		'Enough is a money skill. That sounds simple, but it is rare. Most people know how to want more. More income, more stuff, more status, more upgrades, more proof that they are not behind. More is easy because it does not require much reflection. But if you never define enough, comparison will define it for you. And comparison is brutal because it always has new evidence. Someone has a nicer apartment. Someone travels more. Someone earns more. Someone bought a house. Someone built a business. Someone looks free online. You usually do not see the debt, stress, family help, risk, or tradeoffs behind the image. Without an enough point, your finish line keeps moving. You thought one income would make you calm. Then your lifestyle changed. You thought one purchase would make you feel successful. Then it became normal. Enough does not mean giving up. It does not mean becoming lazy or anti-ambition. It means knowing what money is for. Enough runway to sleep better. Enough spending to enjoy life. Enough income to build options. Enough boundaries to stop chasing every signal. You can still grow. You can still want more. But now more has to answer to your actual life, not someone else’s highlight reel. Enough is the line that keeps ambition useful instead of endless.',
	flashcards: [
		{
			id: 'money-psychology--enough-is-a-money-skill--fc-main',
			role: 'main_idea',
			front: 'What is enough?',
			back: 'A clear personal line for what money needs to do in your life.',
		},
		{
			id: 'money-psychology--enough-is-a-money-skill--fc-watch',
			role: 'watch_out',
			front: 'What moves the finish line?',
			back: 'Comparison, lifestyle creep, status pressure, and undefined goals.',
		},
		{
			id: 'money-psychology--enough-is-a-money-skill--fc-use',
			role: 'use_it',
			front: 'How do you define enough?',
			back: 'Name enough runway, lifestyle, ambition, and freedom in concrete terms.',
		},
	],
	applyAction: {
		type: 'noToolAction',
		label: 'Define your enough point',
		prompt: 'Write one sentence for what “enough” would mean for your money, time, and stress.',
	},
	revision: 1,
};

const ASSESSMENT: ModuleAssessment = {
	moduleId: 'money-psychology',
	checkpointQuiz: [
		{
			type: 'multiple_choice',
			id: 'money-psychology--cq--q1',
			question:
				'What is the main idea behind “money is emotional first”?',
			options: [
				'Money decisions are only math',
				'Emotions often shape behavior before the spreadsheet does',
				'Emotions should always be ignored',
				'Budgeting removes all money stress',
			],
			correctIndex: 1,
			explanation:
				'The lesson teaches that math matters, but fear, stress, pride, identity, and comparison often drive the real decision.',
		},
		{
			type: 'true_false',
			id: 'money-psychology--cq--q2',
			question:
				'A budget can fail if it ignores the emotional pattern behind the spending.',
			correct: true,
			explanation:
				'A budget may not hold if the real issue is avoidance, stress, shame, identity, or social pressure.',
		},
		{
			type: 'scenario',
			id: 'money-psychology--cq--q3',
			scenario:
				'Someone gets a $500 monthly raise, but within two months their new normal spending rises by the full $500.',
			question: 'What happened?',
			options: [
				'They became financially independent',
				'Lifestyle creep absorbed the raise',
				'They eliminated their baseline',
				'Their future self got a vote',
			],
			correctIndex: 1,
			explanation:
				'Lifestyle creep happens when higher income quietly becomes higher normal spending.',
		},
		{
			type: 'multiple_choice',
			id: 'money-psychology--cq--q4',
			question: 'Why is lifestyle creep hard to notice?',
			options: [
				'It always happens in one huge purchase',
				'It hides inside reasonable upgrades',
				'It only happens to high earners',
				'It has nothing to do with income',
			],
			correctIndex: 1,
			explanation:
				'Lifestyle creep often feels normal because each upgrade seems reasonable by itself.',
		},
		{
			type: 'true_false',
			id: 'money-psychology--cq--q5',
			question:
				'Small purchases are always bad and should always be removed.',
			correct: false,
			explanation:
				'The lesson does not shame small joys. It asks whether repeated spending is intentional, valuable, and aligned with bigger goals.',
		},
		{
			type: 'scenario',
			id: 'money-psychology--cq--q6',
			scenario:
				'You spend $8 most workdays on a habit you barely enjoy, while saying you cannot find money for a small goal.',
			question: 'What is the most useful next step?',
			options: [
				'Feel guilty and cut all fun forever',
				'Notice the monthly pattern and decide if the habit is worth it',
				'Ignore it because small money never matters',
				'Blame the habit for every money problem',
			],
			correctIndex: 1,
			explanation:
				'The goal is awareness and intentional choice, not guilt.',
		},
		{
			type: 'multiple_choice',
			id: 'money-psychology--cq--q7',
			question: 'What makes a daily leak important?',
			options: [
				'The purchase is always morally wrong',
				'It is automatic, repeated, and invisible',
				'It involves coffee specifically',
				'It is always larger than rent',
			],
			correctIndex: 1,
			explanation:
				'Daily leaks matter when repeated small spending becomes automatic and steals from goals the user values more.',
		},
	],
	finalTest: [
		{
			type: 'multiple_choice',
			id: 'money-psychology--final--q1',
			question: 'Which statement best captures Module 9?',
			options: [
				'Money behavior is only about knowing formulas',
				'Psychology affects whether money plans survive real life',
				'Spending is always bad',
				'More income automatically fixes emotions',
			],
			correctIndex: 1,
			explanation:
				'Module 9 focuses on the behavioral and emotional patterns behind money choices.',
		},
		{
			type: 'true_false',
			id: 'money-psychology--final--q2',
			question:
				'Money can mean different things to different people, including safety, shame, freedom, status, or comfort.',
			correct: true,
			explanation:
				'The same dollar decision can carry different emotional meanings depending on the person and context.',
		},
		{
			type: 'scenario',
			id: 'money-psychology--final--q3',
			scenario:
				'Luis avoids checking his credit card balance because he feels embarrassed. He tells himself he is “just busy.”',
			question: 'What pattern is most likely happening?',
			options: [
				'Pure math optimization',
				'Emotional avoidance',
				'Future-self planning',
				'Intentional spending',
			],
			correctIndex: 1,
			explanation:
				'Avoiding numbers because of shame or fear is an emotional pattern, not just a time-management issue.',
		},
		{
			type: 'multiple_choice',
			id: 'money-psychology--final--q4',
			question:
				'What is the best way to protect a raise from lifestyle creep?',
			options: [
				'Spend it before thinking',
				'Assign the raise a job before habits absorb it',
				'Never enjoy any of it',
				'Assume income increases solve everything',
			],
			correctIndex: 1,
			explanation:
				'New income is strongest when assigned intentionally to both present value and future options.',
		},
		{
			type: 'scenario',
			id: 'money-psychology--final--q5',
			scenario:
				'A $160 monthly habit genuinely improves your day and fits your plan.',
			question: 'What does the daily-leak lesson suggest?',
			options: [
				'Cut it automatically',
				'Keep it if it is intentional and worth the tradeoff',
				'Hide it from your budget',
				'Feel guilty every time',
			],
			correctIndex: 1,
			explanation:
				'The lesson is about intentionality, not attacking every small joy.',
		},
		{
			type: 'true_false',
			id: 'money-psychology--final--q6',
			question:
				'Present bias means future needs can get ignored because present comfort feels louder.',
			correct: true,
			explanation:
				'Present bias overweights immediate feelings and underweights future consequences.',
		},
		{
			type: 'scenario',
			id: 'money-psychology--final--q7',
			scenario:
				'You want to buy something nonessential because you had a stressful day. It costs $200.',
			question:
				'Which question best brings future self into the decision?',
			options: [
				'Will this make people respect me?',
				'What does this give me now, and what does it cost later?',
				'Can I hide this purchase?',
				'Is this what everyone else is doing?',
			],
			correctIndex: 1,
			explanation:
				'This question balances present comfort with future cost.',
		},
		{
			type: 'multiple_choice',
			id: 'money-psychology--final--q8',
			question: 'Why can “save everything and enjoy nothing” backfire?',
			options: [
				'Because future self does not matter',
				'Because present self may feel punished and rebel',
				'Because spending is always better',
				'Because money plans should be painful',
			],
			correctIndex: 1,
			explanation:
				'A durable plan needs to respect both present life and future options.',
		},
		{
			type: 'multiple_choice',
			id: 'money-psychology--final--q9',
			question: 'What does “enough” protect against?',
			options: [
				'All uncertainty',
				'Endless comparison and moving financial finish lines',
				'The need to earn income',
				'Every future mistake',
			],
			correctIndex: 1,
			explanation:
				'Defining enough helps prevent comparison from setting endless targets.',
		},
		{
			type: 'scenario',
			id: 'money-psychology--final--q10',
			scenario:
				'Priya keeps raising her lifestyle every time her income grows, then feels behind when she sees friends online living differently.',
			question: 'What is the strongest Module 9 diagnosis?',
			options: [
				'She needs a more expensive lifestyle',
				'Her enough point is undefined and comparison is steering the target',
				'She should ignore all goals',
				'Her income is irrelevant',
			],
			correctIndex: 1,
			explanation:
				'Undefined enough points and comparison can keep the finish line moving.',
		},
		{
			type: 'true_false',
			id: 'money-psychology--final--q11',
			question: 'Defining enough means giving up ambition.',
			correct: false,
			explanation:
				'Enough is not anti-ambition. It keeps ambition connected to the life the user actually wants.',
		},
		{
			type: 'scenario',
			id: 'money-psychology--final--q12',
			scenario:
				'Someone says, “I deserve this,” before every unplanned purchase, even when those purchases keep delaying their emergency runway.',
			question: 'What is the best interpretation?',
			options: [
				'Every purchase is automatically justified',
				'The phrase may be covering stress or emotion that needs to be noticed',
				'Emergency runway never matters',
				'They should never spend on themselves',
			],
			correctIndex: 1,
			explanation:
				'Module 9 teaches users to notice the emotion driving the decision instead of accepting every justification at face value.',
		},
	],
};

export const MONEY_PSYCHOLOGY_MODULE: LearningModule = {
	id: 'money-psychology',
	title: 'Money Psychology',
	description:
		'Money is math plus emotion — and emotion usually moves first. Learn to notice the feelings driving your decisions, defend against lifestyle creep and automatic leaks, give your future self a vote, and define what “enough” means before comparison defines it for you.',
	emoji: '🧠',
	accentColor: '#c8ff00',
	tier: 'growth',
	pillar: 'money-psychology-core',
	order: 9,
	lessons: [LESSON_1, LESSON_2, LESSON_3, LESSON_4, LESSON_5],
	assessment: ASSESSMENT,
};
